import { Component, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MainServices } from '../services/MainServices';

interface ChatMessage {
  role: 'user' | 'bot';
  text: string;
}

interface ConversationState {
  waitingForTrackingNumber: boolean;
  lastIntent: string | null;
}

interface KnowledgeIntent {
  id: string;
  keywords: string[];
  response: string;
}

interface QuickAction {
  label: string;
  kind: 'route' | 'url';
  target: string;
}

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ChatbotComponent implements AfterViewChecked {
  @ViewChild('chatMessagesContainer') private chatMessagesContainer!: ElementRef;
  
  isChatOpen = false;
  chatMessages: ChatMessage[] = [];
  chatInput = '';
  isChatLoading = false;
  private shouldScrollToBottom = false;
  conversationState: ConversationState = {
    waitingForTrackingNumber: false,
    lastIntent: null
  };

  readonly quickActions: QuickAction[] = [
    { label: 'Cotizar', kind: 'route', target: '/cotizador' },
    { label: 'Rastrear', kind: 'route', target: '/rastreo' },
    { label: 'Facturacion', kind: 'route', target: '/facturacion' },
    { label: 'Cobertura', kind: 'route', target: '/cobertura' },
    { label: 'Tienda', kind: 'url', target: 'https://pmm.com.mx/tiendapmm' }
  ];

  private readonly trackKeywords = ['rastreo', 'rastrear', 'seguimiento', 'numero de rastreo', 'track', 'donde esta', 'ubicacion de mi', 'estado de mi'];

  private readonly fallbackResponse =
    '🤔 Disculpa, no estoy seguro de entender tu pregunta.\n\n' +
    'Puedes preguntarme sobre:\n' +
    '• Cotizaciones y precios\n' +
    '• Rastreo de envíos\n' +
    '• Facturación\n' +
    '• Compra de guías\n' +
    '• Cobertura y sucursales\n' +
    '• Horarios y contacto\n\n' +
    '💡 Intenta reformular tu pregunta o usa los botones de acceso rápido.';

  private readonly intents: KnowledgeIntent[] = [
    {
      id: 'compra-guias',
      keywords: ['comprar', 'adquirir', 'guia', 'guias', 'prepago', 'prepagas', 'prepagadas', 'tienda', 'consignacion', 'en tu empresa', 'codigo de barras'],
      response:
        '📋 Tienes 3 formas de comprar guías en PMM:\n\n' +
        '1️⃣ Guías prepagadas\n   https://pmm.com.mx/prepago\n\n' +
        '2️⃣ Tienda en línea\n   https://pmm.com.mx/tiendapmm\n\n' +
        '3️⃣ Plataforma empresarial (consignación)\n   http://www.pmmentuempresa.com'
    },
    {
      id: 'volumen-envios',
      keywords: ['volumen', 'envios por mes', 'al mes', 'mensuales', 'paquetes al mes', 'guias al mes', 'por mes', 'mensual'],
      response:
        '📊 Para recomendarte la mejor opción, considera:\n\n' +
        '🔹 Volumen bajo (1-100 envíos/mes):\n   → Guías prepagadas o Tienda en línea\n\n' +
        '🔹 Volumen medio (100-1000 envíos/mes):\n   → Tienda en línea o Plataforma empresarial\n\n' +
        '🔹 Volumen alto (+1000 envíos/mes):\n   → Plataforma empresarial (consignación)\n   http://www.pmmentuempresa.com\n\n' +
        '📞 Para volumen alto, también puedes contactar directamente:\n   Tel: 800-0000766\n   Correo: servicios@pmm.com.mx'
    },
    {
      id: 'servicios',
      keywords: ['servicio', 'servicios', 'que ofrecen', 'entrega', 'ocurre', 'acuse', 'valor declarado', 'retorno', 'detalle', 'cobrar', 'opciones'],
      response:
        '📦 Servicios principales de PMM:\n\n' +
        '✓ Entrega a domicilio\n' +
        '✓ Acuse de recibo\n' +
        '✓ Recolección a domicilio\n' +
        '✓ Servicio por cobrar\n' +
        '✓ Servicio ocurre\n' +
        '✓ Valor declarado\n' +
        '✓ Retorno de evidencias\n' +
        '✓ Entrega a detalle\n\n' +
        'También puedes ver el catálogo completo en la sección Servicios del menú principal.'
    },
    {
      id: 'cotizador',
      keywords: ['cotizar', 'cotizacion', 'cotizador', 'precio', 'costo', 'tarifa', 'cuanto cuesta', 'cp', 'codigo postal', 'cuanto sale'],
      response:
        '💰 Para cotizar tu envío:\n\n' +
        '🌐 Visita:\nhttps://pmm.com.mx/cotizador\n\n' +
        '📝 Datos necesarios:\n' +
        '• CP origen y destino\n' +
        '• Colonia\n' +
        '• Número de sobres o paquetes\n' +
        '• Medidas y peso\n\n' +
        '➕ Servicios adicionales:\n' +
        '• Recolección a domicilio\n' +
        '• Entrega a domicilio\n' +
        '• Valor declarado (máximo $150,000)\n\n' +
        '⚠️ Nota: El total es estimado y puede variar.'
    },
    {
      id: 'rastreo',
      keywords: ['donde esta mi paquete', 'ubicacion paquete', 'estatus envio'],
      response:
        '📍 Para rastrear tu envío:\n\n' +
        '🌐 Visita:\nhttps://pmm.com.mx/rastreo\n\n' +
        '📊 Estados de seguimiento:\n' +
        '• Mercancía documentada\n' +
        '• En tránsito\n' +
        '• En sucursal de destino\n' +
        '• En entrega\n' +
        '• Entregado\n\n' +
        '💡 También puedes preguntarme directamente aquí proporcionando tu número de rastreo.'
    },
    {
      id: 'facturacion',
      keywords: ['factura', 'facturacion', 'cfdi', 'xml', 'pdf', 'folio', 'timbrar', 'fiscal'],
      response:
        '🧾 Facturación electrónica:\n\n' +
        '🌐 Accede a:\nhttps://pmm.com.mx/facturacion\n\n' +
        '🔍 Puedes buscar por:\n' +
        '• Folio de facturación\n' +
        '• Número de rastreo\n\n' +
        '📥 Opciones disponibles:\n' +
        '• Descargar PDF/XML\n' +
        '• Envío por correo\n\n' +
        '⏳ Si no aparece tu factura, puede estar en proceso de timbrado. Intenta más tarde.'
    },
    {
      id: 'cobertura',
      keywords: ['cobertura', 'sucursal', 'sucursales', 'estado', 'ciudad', 'mapa', 'donde tienen', 'ubicaciones'],
      response:
        '🗺️ Consulta cobertura:\n\n' +
        '🌐 Visita:\nhttps://pmm.com.mx/cobertura\n\n' +
        '📍 Funciones disponibles:\n' +
        '• Buscar por estado\n' +
        '• Ver sucursales disponibles\n' +
        '• Mapa interactivo con ubicaciones\n\n' +
        '💡 Encuentra la sucursal más cercana a ti.'
    },
    {
      id: 'recoleccion',
      keywords: ['recoleccion', 'recoleccion a domicilio', 'agendar', 'recoger paquete', 'pasen por', 'recojan'],
      response:
        '🚚 Solicita recolección:\n\n' +
        '🌐 Visita:\nhttps://pmm.com.mx/recoleccion\n\n' +
        '📝 También puedes agregarla como servicio adicional en el cotizador.\n\n' +
        '💡 ¿Necesitas saber qué datos tener listos para agendar más rápido? Pregúntame.'
    },
    {
      id: 'portal-pagos',
      keywords: ['portal de pagos', 'pagos', 'saldo', 'portalpagos', 'pagar', 'mi cuenta', 'estado de cuenta'],
      response:
        '💳 Portal de pagos:\n\n' +
        '🌐 Accede a:\nhttps://pmm.com.mx/portalpagos\n\n' +
        '🔐 Requiere autenticación\n\n' +
        'Si no has iniciado sesión, el sistema te redirige a https://pmm.com.mx/login\n\n' +
        'Desde ahí puedes gestionar pagos y consultar tu saldo.'
    },
    {
      id: 'contacto',
      keywords: ['telefono', 'tel', 'contacto', 'correo', 'email', 'atencion', 'llamar', 'comunicarme', 'hablar'],
      response:
        '📞 Datos de contacto PMM:\n\n' +
        '☎️ Teléfono: 800-0000766\n' +
        '📧 Correo: servicios@pmm.com.mx\n' +
        '💬 WhatsApp: +52 669 270 5424\n\n' +
        '💡 ¿Necesitas ayuda específica? Puedo guiarte para cotizar, rastrear o facturar.'
    },
    {
      id: 'horarios',
      keywords: ['horario', 'horarios', 'hora', 'atienden', 'atencion', 'que hora', 'abierto', 'cerrado'],
      response:
        '🕐 Horario de atención general:\n\n' +
        '📅 Lunes a viernes: 9:00 a 19:00 hrs\n' +
        '   (Horario del Centro)\n\n' +
        '💡 Algunas sucursales pueden tener horarios extendidos. ¿Necesitas información de algún trámite específico?'
    },
    {
      id: 'whatsapp',
      keywords: ['whatsapp', 'wa', 'chat de whatsapp', 'wpp'],
      response:
        '💬 WhatsApp PMM:\n\n' +
        '📱 +52 669 270 5424\n\n' +
        '⏰ Disponible en horario de oficina.'
    },
    {
      id: 'ayuda',
      keywords: ['ayuda', 'faq', 'embalaje', 'empaque', 'proteger envio', 'consejos', 'preguntas frecuentes', 'dudas'],
      response:
        '❓ Ayuda y preguntas frecuentes:\n\n' +
        '🌐 Visita:\nhttps://pmm.com.mx/ayuda\n\n' +
        'Encuentra:\n' +
        '• Consejos de embalaje\n' +
        '• Preguntas frecuentes\n' +
        '• Guías prácticas\n\n' +
        '💡 También hay acceso rápido desde la página de inicio.'
    },
    {
      id: 'saludo',
      keywords: ['hola', 'buenas', 'buen dia', 'que tal', 'hey', 'saludos', 'buenos dias', 'buenas tardes'],
      response:
        '👋 ¡Hola! Soy el asistente virtual de PMM.\n\n' +
        'Puedo ayudarte con:\n' +
        '• Cotizaciones\n' +
        '• Rastreo en tiempo real\n' +
        '• Facturación\n' +
        '• Cobertura y sucursales\n' +
        '• Compra de guías\n' +
        '• Horarios y contacto\n\n' +
        '¿En qué puedo asistirte hoy?'
    }
  ];

  constructor(
    private readonly router: Router,
    private readonly mainServices: MainServices
  ) {}

  toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  async sendMessage() {
    const text = this.chatInput.trim();
    if (!text || this.isChatLoading) return;

    this.chatMessages = [...this.chatMessages, { role: 'user', text }];
    this.chatInput = '';
    this.isChatLoading = true;
    this.shouldScrollToBottom = true;

    try {
      // Simula una breve latencia para mantener UX conversacional sin depender de APIs externas.
      await new Promise(resolve => setTimeout(resolve, 450));

      const botReply = await this.buildReply(text);
      this.chatMessages = [...this.chatMessages, { role: 'bot', text: botReply }];
      this.shouldScrollToBottom = true;
    } catch {
      this.chatMessages = [...this.chatMessages, { role: 'bot', text: 'Error de conexión. Por favor, intenta de nuevo más tarde.' }];
      this.shouldScrollToBottom = true;
    } finally {
      this.isChatLoading = false;
    }
  }

  private scrollToBottom(): void {
    try {
      if (this.chatMessagesContainer) {
        this.chatMessagesContainer.nativeElement.scrollTop = this.chatMessagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      // Ignorar errores de scroll
    }
  }

  onQuickAction(action: QuickAction): void {
    if (action.kind === 'route') {
      if (action.target === '/rastreo') {
        this.clearTrackingCache();
      }
      this.router.navigate([action.target]);
    } else {
      window.location.href = action.target;
    }
    this.isChatOpen = false;
  }

  private clearTrackingCache(): void {
    localStorage.removeItem('rastreo');
    localStorage.removeItem('dataRastreo');
    localStorage.removeItem('numRastreo');
    localStorage.removeItem('error');
  }

  private async buildReply(question: string): Promise<string> {
    const normalizedQuestion = this.normalize(question);

    // Si estamos esperando número de rastreo
    if (this.conversationState.waitingForTrackingNumber) {
      const trackingNumber = this.extractTrackingNumber(question);
      if (!trackingNumber) {
        return '❌ No pude identificar el número de rastreo.\n\nPor favor, proporciona un número válido (10 a 20 caracteres).\n\n📝 Ejemplo: 310945240627C6380';
      }
      this.conversationState.waitingForTrackingNumber = false;
      this.conversationState.lastIntent = null;
      return this.getTrackingReply(trackingNumber);
    }

    // Detectar si es pregunta de rastreo
    if (this.isTrackQuestion(normalizedQuestion)) {
      const trackingNumber = this.extractTrackingNumber(question);
      if (!trackingNumber) {
        // Iniciar flujo conversacional
        this.conversationState.waitingForTrackingNumber = true;
        this.conversationState.lastIntent = 'rastreo';
        return '📦 Claro, puedo ayudarte a rastrear tu envío.\n\nPor favor, proporciona tu número de rastreo o guía empresarial.\n\n📝 Ejemplo: 310945240627C6380';
      }
      return this.getTrackingReply(trackingNumber);
    }

    const ranked = this.intents
      .map(intent => ({
        intent,
        score: intent.keywords.reduce((acc, keyword) => acc + (normalizedQuestion.includes(this.normalize(keyword)) ? 1 : 0), 0)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    if (!ranked.length) {
      return this.fallbackResponse;
    }

    const topAnswers = ranked.slice(0, 2).map(item => item.intent.response);
    return topAnswers.join('\n\n');
  }

  private isTrackQuestion(normalizedQuestion: string): boolean {
    return this.trackKeywords.some(keyword => normalizedQuestion.includes(this.normalize(keyword)));
  }

  private extractTrackingNumber(question: string): string | null {
    const matches = question.toUpperCase().match(/[A-Z0-9]{10,20}/g);
    return matches && matches.length ? matches[0] : null;
  }

  private async getTrackingReply(trackingNumber: string): Promise<string> {
    try {
      // GET /rastreo?numero= devuelve la carga util directa; un 404/409/422
      // rechaza la promesa y lo atrapa el catch de abajo.
      const rastreo = await this.mainServices.getRastroGuia(trackingNumber);
      if (!rastreo) {
        return `❌ No encontré resultados para la guía ${trackingNumber}.\n\nPor favor, verifica el número e intenta nuevamente.`;
      }

      if (rastreo.cancelado === true) {
        return `⚠️ La guía ${trackingNumber} aparece como cancelada en el sistema.`;
      }

      const guia = rastreo.guia;
      const detalle = rastreo.detalle || [];
      
      // Obtener el último movimiento del array detalle
      let ultimoMovimiento = '';
      let fechaUltimoMovimiento = '';
      let ubicacionUltimoMovimiento = '';
      
      if (detalle.length > 0) {
        // El array detalle está ordenado cronológicamente invertido, index 0 es el más reciente
        const ultimoDetalle = detalle[0];
        ultimoMovimiento = ultimoDetalle.estado || 'Sin estado';
        fechaUltimoMovimiento = this.formatDate(ultimoDetalle.fecha);
        ubicacionUltimoMovimiento = ultimoDetalle.ubicacion || 'No disponible';
      } else {
        ultimoMovimiento = guia && guia.estado ? guia.estado : 'Sin estatus disponible';
        fechaUltimoMovimiento = this.formatDate(guia && guia.fecha ? guia.fecha : null);
        ubicacionUltimoMovimiento = guia && guia.ubicacion ? guia.ubicacion : 'No disponible';
      }

      const origen = guia && guia.origen ? guia.origen : 'No disponible';
      const destino = guia && guia.destino ? guia.destino : 'No disponible';
      const destinatario = guia && guia.destinatario ? guia.destinatario : 'No disponible';
      const estadoGeneral = guia && guia.estado ? guia.estado : 'Sin estado';

      return (
        `📦 Rastreo de guia ${trackingNumber}:\n\n` +
        `Estado general: ${estadoGeneral}\n\n` +
        `Origen: ${origen}\n` +
        `Destino: ${destino}\n` +
        `Destinatario: ${destinatario}\n\n` +
        `━━━━━━━━━━━━━━━━━━━━\n\n` +
        `🔔 Ultimo movimiento:\n\n` +
        `${ultimoMovimiento}\n\n` +
        `Fecha: ${fechaUltimoMovimiento}\n` +
        `Ubicacion: ${ubicacionUltimoMovimiento}\n\n` +
        `━━━━━━━━━━━━━━━━━━━━\n\n` +
        '🌐 Ver historial completo:\nhttps://pmm.com.mx/rastreo'
      );
    } catch {
      return '⚠️ No pude consultar el rastreo en este momento.\n\nPor favor, intenta de nuevo en unos segundos.';
    }
  }

  private formatDate(value: string | Date | null): string {
    if (!value) {
      return 'No disponible';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'No disponible';
    }
    return date.toLocaleDateString('es-MX');
  }

  private normalize(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  linkifyText(text: string): string {
    // Regex para detectar URLs (http, https, www)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    return text.replace(urlRegex, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="chat-link">${url}</a>`;
    });
  }
}
